import React, { forwardRef } from 'react';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
}

/**
 * KERN Textarea Component
 *
 * Usage:
 * <Textarea label="Nachricht" name="message" rows={5} />
 * <Textarea label="Beschreibung" error="Feld ist erforderlich" />
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, hint, error, optional, id, className = '', ...props }, ref) => {
    const textareaId = id || `textarea-${label.toLowerCase().replace(/\s+/g, '-')}`;
    const hintId = hint ? `${textareaId}-hint` : undefined;
    const errorId = error ? `${textareaId}-error` : undefined;
    const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

    const wrapperClasses = ['kern-form-input', error && 'kern-form-input--error', className]
      .filter(Boolean)
      .join(' ');

    const textareaClasses = ['kern-form-input__input', error && 'kern-form-input__input--error']
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        <label className="kern-label" htmlFor={textareaId}>
          {label}
          {optional && <span className="kern-label__optional"> - Optional</span>}
        </label>
        {hint && (
          <div className="kern-hint" id={hintId}>
            {hint}
          </div>
        )}
        <textarea
          ref={ref}
          className={textareaClasses}
          id={textareaId}
          aria-describedby={describedBy}
          {...props}
        />
        {error && (
          <p className="kern-error" id={errorId} role="alert">
            <span className="kern-icon kern-icon--danger kern-icon--md" aria-hidden="true" />
            <span className="kern-body">{error}</span>
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
